import React, { useEffect, useState, useCallback, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { getSavingsRate } from "../../api/savingsClient";
import { formatCurrency, formatCurrencyCompact, formatPercent } from "../../utils/formatters";
import TransactionReviewModal from "./TransactionReviewModal";
import ChartLoader from "../ui/ChartLoader";

function SavingsTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-md text-sm">
            <p className="text-gray-500 text-xs mb-1">{d.label}</p>
            <p className="text-gray-700">Income: {formatCurrency(d.income)}</p>
            <p className="text-gray-700">Expenses: {formatCurrency(d.expenses)}</p>
            <p className={`font-medium ${d.netSavings >= 0 ? "text-accent-600" : "text-danger-500"}`}>
                Saved: {formatCurrency(d.netSavings)}
            </p>
            <p className={`font-medium ${d.savingsRate >= 0 ? "text-accent-600" : "text-danger-500"}`}>
                Rate: {formatPercent(d.savingsRate)}
            </p>
        </div>
    );
}

export default function SavingsRateCard({ months = 6, onMonthsChange, syncing = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showReview, setShowReview] = useState(false);
    const abortRef = useRef(null);

    const fetchData = useCallback(async () => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        setLoading(true);
        setError(null);

        try {
            const resp = await getSavingsRate(months, abortRef.current.signal);
            setData(resp);
        } catch (e) {
            if (e.name === "AbortError" || e.name === "CanceledError") return;
            setError("Failed to load savings data");
        } finally {
            setLoading(false);
        }
    }, [months]);

    useEffect(() => {
        fetchData();
        return () => abortRef.current?.abort();
    }, [fetchData]);

    if (loading) {
        return (
            <div className="card p-6">
                <div className="h-5 bg-gray-100 rounded w-32 mb-4 animate-pulse" />
                <ChartLoader height={96} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="card p-6">
                <p className="text-gray-500 text-sm">{error}</p>
            </div>
        );
    }

    if (!data || data.months.length === 0) {
        return (
            <div className="card p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Savings rate</h3>
                {syncing ? (
                    <div className="flex items-center gap-3 py-4">
                        <div className="animate-spin w-5 h-5 border-2 border-gray-200 border-t-accent-500 rounded-full" />
                        <p className="text-gray-500 text-sm">Syncing your financial data...</p>
                    </div>
                ) : (
                    <p className="text-gray-500 text-sm">
                        Not enough transaction data yet. Link your accounts and check back after a month.
                    </p>
                )}
            </div>
        );
    }

    const chartData = data.months.map(m => {
        const date = new Date(m.month + "T00:00:00");
        return {
            label: date.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
            income: m.income,
            expenses: m.expenses,
            netSavings: m.netSavings,
            savingsRate: m.savingsRate,
        };
    });

    const rate = data.savingsRate;
    const rateColor = rate >= 50 ? "text-accent-600" : rate >= 20 ? "text-gray-900" : "text-danger-500";

    return (
        <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-gray-900">Savings rate</h3>
                        {onMonthsChange && (
                            <div className="flex gap-1 text-xs">
                                {[1, 3, 6].map(m => {
                                    const available = data.monthsAvailable ?? 6;
                                    const wouldShowSame = m > available && months <= available;
                                    return (
                                        <button
                                            key={m}
                                            onClick={() => onMonthsChange(m)}
                                            className={`px-2 py-0.5 rounded font-medium ${
                                                months === m
                                                    ? "bg-accent-50 text-accent-600"
                                                    : wouldShowSame
                                                        ? "text-gray-300 cursor-default"
                                                        : "text-gray-500 hover:text-gray-900"
                                            }`}
                                            title={wouldShowSame ? `Only ${available} month${available !== 1 ? "s" : ""} of data available` : undefined}
                                        >
                                            {m}mo
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => setShowReview(true)}
                        className="text-xs text-gray-500 hover:text-accent-600 transition-colors"
                    >
                        Review transactions
                    </button>
                </div>
                <span className={`text-2xl font-semibold tracking-tight ${rateColor}`}>
                    {formatPercent(rate)}
                </span>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                <div>
                    <p className="text-gray-500 text-xs">Avg income</p>
                    <p className="text-gray-900 font-medium">{formatCurrencyCompact(data.avgMonthlyIncome)}</p>
                </div>
                <div>
                    <p className="text-gray-500 text-xs">Avg expenses</p>
                    <p className="text-gray-900 font-medium">{formatCurrencyCompact(data.avgMonthlyExpenses)}</p>
                </div>
                <div>
                    <p className="text-gray-500 text-xs">Avg saved</p>
                    <p className={`font-medium ${data.avgMonthlySavings >= 0 ? "text-accent-600" : "text-danger-500"}`}>
                        {formatCurrencyCompact(data.avgMonthlySavings)}
                    </p>
                </div>
            </div>

            {chartData.length > 1 && (
                <div className="h-40" style={{ willChange: "transform" }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} barGap={2}>
                            <XAxis
                                dataKey="label"
                                tick={{ fill: "#64748B", fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{ fill: "#64748B", fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v) => formatCurrencyCompact(v)}
                                width={60}
                            />
                            <Tooltip content={<SavingsTooltip />} cursor={{ fill: "#F1F5F9" }} />
                            <ReferenceLine y={0} stroke="#CBD5E1" />
                            <Bar
                                dataKey="netSavings"
                                radius={[4, 4, 0, 0]}
                                fill="#2563EB"
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            <TransactionReviewModal
                isOpen={showReview}
                onClose={() => setShowReview(false)}
                onChanged={fetchData}
            />
        </div>
    );
}
